import { ref, computed, reactive, watch } from 'vue';
import axios from 'axios';

/**
 * Composable for managing project advancement configuration.
 * Supports both simple mode (same topCut for all disciplines) and
 * advanced mode (per-discipline topCut overrides).
 */
export function useAdvancementConfig(eventDisciplines = []) {
  // Mode: 'simple' or 'advanced'
  const mode = ref('simple');

  // Simple mode: Single topCut value for all disciplines
  const defaultTopCut = ref(1);

  // Advanced mode: Per-discipline overrides
  // Structure: { 'Computer Science': 3, 'Electrical Engineering': 2, ... }
  const disciplineOverrides = reactive({});

  // Preview data
  const previewData = ref(null);
  const isLoadingPreview = ref(false);
  const previewError = ref(null);

  // Validation errors
  const validationErrors = ref([]);

  /**
   * Computed property: Is the configuration in simple mode?
   */
  const isSimpleMode = computed(() => mode.value === 'simple');

  /**
   * Computed property: Is the configuration in advanced mode?
   */
  const isAdvancedMode = computed(() => mode.value === 'advanced');

  /**
   * Computed property: Build the AdvancementConfigReq object for API calls
   */
  const configPayload = computed(() => {
    const payload = {
      defaultTopCut: defaultTopCut.value
    };

    // Only include disciplineOverrides if in advanced mode and has overrides
    if (mode.value === 'advanced' && Object.keys(disciplineOverrides).length > 0) {
      payload.disciplineOverrides = { ...disciplineOverrides };
    }

    return payload;
  });

  /**
   * Computed property: Get summary description of current configuration
   */
  const configSummary = computed(() => {
    if (mode.value === 'simple') {
      return `Simple mode: Top ${defaultTopCut.value} project(s) from each discipline`;
    }

    const overrideCount = Object.keys(disciplineOverrides).length;
    if (overrideCount === 0) {
      return `Advanced mode: Top ${defaultTopCut.value} project(s) from each discipline (no overrides)`;
    }

    const overrides = Object.entries(disciplineOverrides)
      .map(([disc, topCut]) => `${disc}: ${topCut}`)
      .join(', ');
    return `Advanced mode: Default ${defaultTopCut.value}, Overrides: ${overrides}`;
  });

  /**
   * Computed property: Check if configuration is valid
   */
  const isValid = computed(() => {
    return validationErrors.value.length === 0 && defaultTopCut.value >= 0;
  });

  /**
   * Switch to simple mode
   */
  function switchToSimpleMode() {
    mode.value = 'simple';
    // Clear advanced mode overrides
    Object.keys(disciplineOverrides).forEach(key => {
      delete disciplineOverrides[key];
    });
    validateConfig();
  }

  /**
   * Switch to advanced mode
   */
  function switchToAdvancedMode() {
    mode.value = 'advanced';
    validateConfig();
  }

  /**
   * Set the default topCut value
   */
  function setDefaultTopCut(value) {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      defaultTopCut.value = parsed;
      validateConfig();
    }
  }

  /**
   * Set a per-discipline override
   */
  function setDisciplineOverride(discipline, value) {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      disciplineOverrides[discipline] = parsed;
      validateConfig();
    }
  }

  /**
   * Remove a per-discipline override
   */
  function removeDisciplineOverride(discipline) {
    delete disciplineOverrides[discipline];
    validateConfig();
  }

  /**
   * Get the effective topCut for a discipline
   * (returns override if exists, otherwise returns defaultTopCut)
   */
  function getTopCutForDiscipline(discipline) {
    if (mode.value === 'advanced' && disciplineOverrides[discipline] !== undefined) {
      return disciplineOverrides[discipline];
    }
    return defaultTopCut.value;
  }

  /**
   * Validate the current configuration
   */
  function validateConfig() {
    const errors = [];

    // Validate defaultTopCut
    if (defaultTopCut.value < 0) {
      errors.push('Default topCut must be >= 0');
    }

    if (defaultTopCut.value > 1000) {
      errors.push('Default topCut exceeds 1000, which is unusually high. Please verify this is intentional.');
    }

    // Validate discipline overrides
    if (mode.value === 'advanced') {
      Object.entries(disciplineOverrides).forEach(([discipline, value]) => {
        if (value < 0) {
          errors.push(`Discipline override for '${discipline}' must be >= 0`);
        }

        if (value > 1000) {
          errors.push(`Discipline override for '${discipline}' exceeds 1000, which is unusually high.`);
        }

        // Check if discipline exists in event's disciplines
        if (eventDisciplines.length > 0 && !eventDisciplines.includes(discipline)) {
          errors.push(`Unknown discipline '${discipline}'. Valid disciplines: ${eventDisciplines.join(', ')}`);
        }
      });

      // Check for duplicate keys (shouldn't happen with reactive object, but defensive)
      const normalizedKeys = Object.keys(disciplineOverrides).map(k => k.trim().toLowerCase());
      const uniqueKeys = new Set(normalizedKeys);
      if (normalizedKeys.length !== uniqueKeys.size) {
        errors.push('Duplicate discipline names detected (case-insensitive)');
      }
    }

    validationErrors.value = errors;
    return errors.length === 0;
  }

  /**
   * Fetch advancement preview from the API
   * @param {string} eventId - The event ID to preview advancement for
   */
  async function fetchPreview(eventId) {
    if (!validateConfig()) {
      previewError.value = 'Configuration is invalid. Please fix errors before previewing.';
      return null;
    }

    isLoadingPreview.value = true;
    previewError.value = null;
    previewData.value = null;

    try {
      const payload = configPayload.value;
      console.log('[useAdvancementConfig] Sending preview request:', {
        url: `events/tabulate/${eventId}/preview`,
        payload: payload
      });

      const response = await axios.post(
        `events/tabulate/${eventId}/preview`,
        payload
      );
      previewData.value = response.data;
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.description || error.response?.data?.Description || error.message || 'Failed to load preview';
      previewError.value = errorMsg;
      console.error('[useAdvancementConfig] Preview error:', error);
      console.error('[useAdvancementConfig] Error response:', error.response?.data);
      throw error;
    } finally {
      isLoadingPreview.value = false;
    }
  }

  /**
   * Clear preview data
   */
  function clearPreview() {
    previewData.value = null;
    previewError.value = null;
  }

  /**
   * Reset configuration to defaults
   */
  function resetConfig() {
    mode.value = 'simple';
    defaultTopCut.value = 1;
    Object.keys(disciplineOverrides).forEach(key => {
      delete disciplineOverrides[key];
    });
    clearPreview();
    validateConfig();
  }

  /**
   * Initialize configuration from API response or existing data
   */
  function initFromData(data) {
    if (data.defaultTopCut !== undefined) {
      defaultTopCut.value = data.defaultTopCut;
    }

    if (data.disciplineOverrides && Object.keys(data.disciplineOverrides).length > 0) {
      mode.value = 'advanced';
      Object.keys(disciplineOverrides).forEach(key => {
        delete disciplineOverrides[key];
      });
      Object.assign(disciplineOverrides, data.disciplineOverrides);
    } else {
      mode.value = 'simple';
    }

    validateConfig();
  }

  // Auto-validate when defaultTopCut or disciplineOverrides change
  watch(() => defaultTopCut.value, () => validateConfig());
  watch(() => disciplineOverrides, () => validateConfig(), { deep: true });

  // Initialize validation
  validateConfig();

  return {
    // State
    mode,
    defaultTopCut,
    disciplineOverrides,
    previewData,
    isLoadingPreview,
    previewError,
    validationErrors,

    // Computed
    isSimpleMode,
    isAdvancedMode,
    configPayload,
    configSummary,
    isValid,

    // Methods
    switchToSimpleMode,
    switchToAdvancedMode,
    setDefaultTopCut,
    setDisciplineOverride,
    removeDisciplineOverride,
    getTopCutForDiscipline,
    validateConfig,
    fetchPreview,
    clearPreview,
    resetConfig,
    initFromData
  };
}
